/**
 * Single Zustand store for the workflow editor.
 *
 * Replaces both ``WorkflowEditorContext`` (React Context inside the route) and
 * ``workflowEditorBridge`` (mirror Zustand store for the panel layout). All
 * workflow-editor state lives here; both in-route components (canvas, nodes)
 * and out-of-route components (DefaultPanelLayout, Subnav) read the same
 * store directly — no mirroring, no signature-based dedup, no BridgeSync.
 */
import { create } from "zustand";
import type { Edge, Node } from "@xyflow/react";
import type { TriggerNodeData, ActionNodeData } from "./types/nodeData";
import type { RuleFragment } from "@/__generated__/graphql";

export type AnyNodeData = TriggerNodeData | ActionNodeData;

// ── Helpers ──────────────────────────────────────────────────────────────

// ── Store shape ──────────────────────────────────────────────────────────

interface RuleEditorState {
  // Data
  rule: RuleFragment | null;
  nodes: Node<AnyNodeData>[];
  edges: Edge[];
  /** GraphQL node payloads keyed by Relay id — canvas nodes read this directly. */
  serverNodesById: Record<string, unknown>;
  selectedNodeId: string | null;
  selectedNodeTitle: string | null;
  selectedNodeType: string | null;
  addEdgeTarget: string;
  isLayoutAnimating: boolean;

  // Actions — populated by the route-level hook that owns React Flow
  setRule: (rule: RuleFragment | null) => void;
  updateRule: (updater: (r: RuleFragment) => RuleFragment) => void;
  setNodes: React.Dispatch<React.SetStateAction<Node<AnyNodeData>[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  setSelectedNodeId: (id: string | null) => void;
  setAddEdgeTarget: (target: string) => void;
  updateSelectedNodeData: (updater: (data: AnyNodeData) => AnyNodeData) => void;
  updateNodeData: (
    nodeId: string,
    updater: (data: AnyNodeData) => AnyNodeData,
  ) => void;
  addEdgeFromPanel: (targetNodeId: string) => void;
  removeEdge: (edgeId: string) => void;
  updateEdgeLabel: (edgeId: string, label: string) => void;
  createNode: (nodeType: string, defaultLabel: string) => void;
  organizeNodes: () => void;
  setIsLayoutAnimating: (v: boolean) => void;

  // Batch-set for the route-level hook to hydrate all action refs at once
  _setActions: (
    actions: Partial<
      Pick<
        RuleEditorState,
        | "setRule"
        | "updateRule"
        | "setNodes"
        | "setEdges"
        | "setSelectedNodeId"
        | "setAddEdgeTarget"
        | "updateSelectedNodeData"
        | "updateNodeData"
        | "addEdgeFromPanel"
        | "removeEdge"
        | "updateEdgeLabel"
        | "createNode"
        | "organizeNodes"
        | "setIsLayoutAnimating"
      >
    >,
  ) => void;

  reset: () => void;
}

const NOOP = () => {};
const NOOP_SET: React.Dispatch<React.SetStateAction<any>> = () => {};

const INITIAL_STATE: Omit<RuleEditorState, "_setActions" | "reset"> = {
  rule: null,
  nodes: [],
  edges: [],
  serverNodesById: {},
  selectedNodeId: null,
  selectedNodeTitle: null,
  selectedNodeType: null,
  addEdgeTarget: "",
  isLayoutAnimating: false,
  setRule: NOOP,
  updateRule: NOOP,
  setNodes: NOOP_SET,
  setEdges: NOOP_SET,
  setSelectedNodeId: NOOP,
  setAddEdgeTarget: NOOP,
  updateSelectedNodeData: NOOP,
  updateNodeData: NOOP,
  addEdgeFromPanel: NOOP,
  removeEdge: NOOP,
  updateEdgeLabel: NOOP,
  createNode: NOOP,
  organizeNodes: NOOP,
  setIsLayoutAnimating: NOOP,
};

export const useRuleEditorStore = create<RuleEditorState>()((set) => ({
  ...INITIAL_STATE,
  _setActions: (actions) => set(actions),
  reset: () => set(INITIAL_STATE),
}));
